import pick from 'lodash/pick'
import omit from 'lodash/omit'
import {
    Navigate,
} from 'react-router-dom'

import {
    CommonRoutes,
} from 'app/enums/common.enums'

import {
    SidebarTab,
} from 'app/shared-components/SkyNetDomain'

import {
    ActionComponentsKey,
    ActionComponentsType, SkyNetDomainConfigParams, SkyNetMasterTabConfig,
} from 'app/PoC/DataGridDemoPages/SkyNetDomainV2/SkyNetDomain.types'
import SkyNetDomainFormWrapper
    from 'app/PoC/DataGridDemoPages/SkyNetDomainV2/SkyNetDomainForm/SkyNetDomainFormWrapper'
import SkyNetDomainFormTabs
    from 'app/PoC/DataGridDemoPages/SkyNetDomainV2/SkyNetDomainForm/SkyNetDomainFormTabs'
import SkyNetDomainFormAction from './SkyNetDomainForm/SkyNetDomainFormAction'

function isFormViewsFn(
    formViews: SidebarTab[] | ((params?: Record<string, any>) => SidebarTab[]),
): formViews is (params?: Record<string, any>) => SidebarTab[] {
    return typeof formViews === 'function'
}

const actionsMap: Record<ActionComponentsKey, string> = {
    [ActionComponentsKey.Create]: 'create',
    [ActionComponentsKey.Print]: 'print',
    [ActionComponentsKey.BulkCreate]: 'bulk-create',
    [ActionComponentsKey.BulkEdit]: 'bulk-edit',
}

const createActionRoutes = (
    tabs: SkyNetMasterTabConfig<any>[],
    defaultActionComponents: ActionComponentsType,
    uniqField: string,
) => {
    return tabs.map((tab) => {
        const actionComponents = tab.actionComponents || defaultActionComponents

        const actions = pick(actionComponents, [
            ActionComponentsKey.Create,
            ActionComponentsKey.Print,
        ]) as ActionComponentsType

        return Object.entries(actions).map(([
            action,
            {
                component, ...rest
            },
        ]) => {
            return {
                path: `${tab.url}/${actionsMap[action]}/${CommonRoutes.Asterisk}`,
                element: <SkyNetDomainFormAction
                    component={component}
                    uniqField={uniqField}
                />,
                ...rest,
            }
        })
    }).flat()
}

const createBulkActionRoute = (actionComponents: ActionComponentsType = {}) => {
    // if we decide to display them in fullscreen,
    // need to move them to the same level as createActionRoutes
    const actions = pick(actionComponents, [
        ActionComponentsKey.BulkCreate,
        ActionComponentsKey.BulkEdit,
    ]) as ActionComponentsType

    return Object.entries(actions).map(([
        action,
        {
            component, ...rest
        },
    ]) => {
        const Component = component

        return {
            path: `${actionsMap[action]}/${CommonRoutes.Asterisk}`,
            element: <Component onClose={() => {
            }} // TODO-DK: implement bulk actions wrapper component
            />,
            ...rest,
        }
    })
}

// eslint-disable-next-line import/prefer-default-export
export const createSkyNetDomainRoutes = (root: JSX.Element, {
    config,
    formViews,
    actionComponents = {},
    defaultTab = 0,
    uniqField = 'id',
}: SkyNetDomainConfigParams) => {
    return [
        ...config.map((tab) => {
            let forms: SidebarTab[]

            if (tab.formViews) {
                forms = isFormViewsFn(tab.formViews)
                    ? tab.formViews()
                    : tab.formViews
            } else {
                forms = isFormViewsFn(formViews)
                    ? formViews()
                    : formViews || []
            }

            const actions = tab.actionComponents || actionComponents

            return {
                path: tab.url,
                element: root,
                ...omit(tab, [
                    'url',
                    'formViews',
                    'actionComponents',
                ]),
                children: [
                    {
                        path: `${CommonRoutes.Edit}/:key/${CommonRoutes.Asterisk}`,
                        element: <SkyNetDomainFormTabs forms={forms} />,
                        children: [
                            ...forms.map((formView) => {
                                return {
                                    path: formView.url,
                                    element: (
                                        <SkyNetDomainFormWrapper
                                            component={formView.Component}
                                        />
                                    ),
                                    ...omit(formView, 'Component'),
                                }
                            }),
                            {
                                path: CommonRoutes.Index,
                                element: <Navigate
                                    to={forms[0]?.url || '.'}
                                    replace
                                />,
                            },
                        ],
                    },
                    ...createBulkActionRoute(actions),
                ],
            }
        }),
        ...createActionRoutes(config, actionComponents, uniqField),
        {
            path: CommonRoutes.Index,
            element: <Navigate
                to={config[defaultTab]?.url}
                replace
            />,
        },
    ]
}
